import React from 'react'
import {
  fetchAssetInventoryExecutiveSummary,
  type AssetInventoryExecutiveAsset,
  type AssetInventoryExecutiveSummary,
} from '../../services/enterpriseOperationalUxApi'
import type { InventoryAssetRef } from '../assets/AssetInventoryIntelligenceWorkspace'
import { formatAssetTargetLabel } from '../../utils/gvmHosts'
export type AssetOverviewStats = {
  totalAssets: number
  visibleAssets: number
  unknownAssets: number
  assetsWithVulnerabilities: number
  totalVulnerabilities: number
  criticalVulnerabilities: number
  highVulnerabilities: number
  mediumVulnerabilities?: number
  lowVulnerabilities?: number
  avgRiskScore: number
  newAssetsLast7Days?: number
  complianceAvg?: number
  edrCoverage?: number
}

export type AssetInventoryExecutiveContextValue = {
  overviewStats?: AssetOverviewStats | null
  assets: InventoryAssetRef[]
  searchQuery: string
  filterAssetIds: Set<string> | null
  summary: AssetInventoryExecutiveSummary | null
  loading: boolean
  graphOpen: boolean
  setGraphOpen: React.Dispatch<React.SetStateAction<boolean>>
  selectedId: string | null
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>
  selection: import('./AssetInventoryExecutiveContextPanel').AssetInventorySelection
  setSelection: React.Dispatch<
    React.SetStateAction<import('./AssetInventoryExecutiveContextPanel').AssetInventorySelection>
  >
  mergedExecutiveAssets: AssetInventoryExecutiveAsset[]
  filteredExecutive: AssetInventoryExecutiveAsset[]
  selectedAsset: AssetInventoryExecutiveAsset | null
  posture: AssetInventoryExecutiveSummary['postureStrip'] | undefined
  loadSummary: () => Promise<void>
  onEdit?: (assetId: string) => void
  onDelete?: (assetId: string) => void
  onScan?: (asset: InventoryAssetRef) => void
  onOpenSystem?: (systemId: string) => void
}

const Ctx = React.createContext<AssetInventoryExecutiveContextValue | null>(null)

function assetKey(a: InventoryAssetRef): string {
  return String(a.id || a.assetId || '')
}

function networkLabelFromRef(ref: InventoryAssetRef): string {
  if (ref.networkTarget?.trim()) return ref.networkTarget.trim()
  return formatAssetTargetLabel({
    name: ref.name,
    ipAddress: ref.ipAddress,
    details: ref.details,
  })
}

function buildFallbackExecutiveAsset(ref: InventoryAssetRef): AssetInventoryExecutiveAsset {
  const id = assetKey(ref)
  const networkTarget = networkLabelFromRef(ref)
  return {
    assetId: id,
    name: ref.name,
    type: ref.type,
    environment: ref.environment ?? null,
    criticality: ref.criticality ?? null,
    networkTarget: networkTarget || null,
    lifecycleStatus: null,
    linkedSystemsCount: 0,
    linkedInformationSystems: [],
    exposure: {
      internetFacing: false,
      production: String(ref.environment || '').toLowerCase().includes('prod'),
      externallyAccessible: false,
      exposureLevel: null,
      indicators: [],
    },
    complianceApplicability: {
      standardsAffected: [],
      inheritedScope: [],
      directScope: [],
    },
    pressure: {
      band: 'LOW',
      operationalPressure: 0,
      degradedControls: 0,
      staleEvidenceCount: 0,
      blockers: 0,
    },
    assurance: {
      assuranceLevel: 'MANUAL',
      controlHealth: null,
      telemetryPresent: false,
    },
    vulnerability: {
      count: 0,
      criticalCount: 0,
      topVulnerabilities: [],
    },
    blastRadius: {
      hits: [],
      impactedStandards: [],
      contribution: 0,
    },
    workflow: {
      stages: [],
      degraded: false,
    },
    runtime: {
      latestEvent: null,
      recentEvents: [],
    },
    storytelling: null,
    recommendedNextActions: [],
  }
}

type ProviderProps = {
  overviewStats?: AssetOverviewStats | null
  assets: InventoryAssetRef[]
  searchQuery?: string
  filterAssetIds?: Set<string> | null
  onEdit?: (assetId: string) => void
  onDelete?: (assetId: string) => void
  onScan?: (asset: InventoryAssetRef) => void
  onOpenSystem?: (systemId: string) => void
  children: React.ReactNode
}

export function AssetInventoryExecutiveProvider({
  overviewStats,
  assets,
  searchQuery = '',
  filterAssetIds = null,
  onEdit,
  onDelete,
  onScan,
  onOpenSystem,
  children,
}: ProviderProps) {
  const [summary, setSummary] = React.useState<AssetInventoryExecutiveSummary | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [graphOpen, setGraphOpen] = React.useState(true)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [selection, setSelection] = React.useState<
    import('./AssetInventoryExecutiveContextPanel').AssetInventorySelection
  >(null)

  const loadSummary = React.useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchAssetInventoryExecutiveSummary()
      setSummary(data?.enterpriseUxEnabled !== false ? data : null)
    } catch {
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadSummary()
  }, [loadSummary, assets.length])

  const assetsById = React.useMemo(() => {
    const map = new Map<string, AssetInventoryExecutiveAsset>()
    for (const a of summary?.assets || []) {
      map.set(String(a.assetId), a)
    }
    return map
  }, [summary?.assets])

  const mergedExecutiveAssets = React.useMemo(() => {
    return assets.map((ref) => {
      const id = assetKey(ref)
      const ex = assetsById.get(id)
      if (ex) {
        const networkTarget =
          ex.networkTarget?.trim() || networkLabelFromRef(ref) || null
        return {
          ...ex,
          name: ex.name || ref.name,
          type: ex.type || ref.type,
          environment: ex.environment ?? ref.environment ?? null,
          criticality: ex.criticality ?? ref.criticality ?? null,
          networkTarget,
        }
      }
      return buildFallbackExecutiveAsset(ref)
    })
  }, [assets, assetsById])

  const filteredExecutive = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return mergedExecutiveAssets.filter((ex) => {
      const id = String(ex.assetId)
      if (filterAssetIds && !filterAssetIds.has(id)) return false
      if (!q) return true
      return (
        ex.name.toLowerCase().includes(q) ||
        ex.type.toLowerCase().includes(q) ||
        (ex.environment || '').toLowerCase().includes(q) ||
        (ex.networkTarget || '').toLowerCase().includes(q) ||
        (ex.ownerLabel || '').toLowerCase().includes(q)
      )
    })
  }, [mergedExecutiveAssets, searchQuery, filterAssetIds])

  const selectedAsset = selectedId
    ? mergedExecutiveAssets.find((a) => String(a.assetId) === selectedId) ?? null
    : null

  const value: AssetInventoryExecutiveContextValue = {
    overviewStats,
    assets,
    searchQuery,
    filterAssetIds,
    summary,
    loading,
    graphOpen,
    setGraphOpen,
    selectedId,
    setSelectedId,
    selection,
    setSelection,
    mergedExecutiveAssets,
    filteredExecutive,
    selectedAsset,
    posture: summary?.postureStrip,
    loadSummary,
    onEdit,
    onDelete,
    onScan,
    onOpenSystem,
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAssetInventoryExecutive() {
  const ctx = React.useContext(Ctx)
  if (!ctx) {
    throw new Error('useAssetInventoryExecutive must be used within AssetInventoryExecutiveProvider')
  }
  return ctx
}
